import { useEffect, useMemo, useState, useCallback } from 'react';
import { listCourses, archiveCourse, deleteCourse } from '../api/coursesApi';
import { appToast } from '../../../../components/AppToast';
import { appConfirm } from '../../../../components/AppConfirmModal';

const DEFAULT_PAGE_SIZE = 10;

export function useCoursesListScreen() {
  const [viewMode, setViewMode] = useState('active'); // 'active' | 'archived' | 'all'
  const [searchText, setSearchText] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [items, setItems] = useState([]);
  const [meta, setMeta] = useState({
    totalCount: 0,
    page: 1,
    pageSize: DEFAULT_PAGE_SIZE,
    totalPages: 1,
  });

  const queryParams = useMemo(() => {
    return {
      page,
      pageSize,
      viewMode,
      searchText: searchText.trim(),
    };
  }, [page, pageSize, viewMode, searchText]);

  const loadCourses = useCallback(async () => {
    setIsLoading(true);
    setError('');
    try {
      const response = await listCourses(queryParams);
      setItems(response.items || []);
      setMeta({
        totalCount: response.total_count ?? 0,
        page: response.page ?? 1,
        pageSize: response.page_size ?? pageSize,
        totalPages: response.total_pages ?? 1,
      });
    } catch (requestError) {
      const msg = requestError?.response?.data?.detail || 'Failed to load courses.';
      setError(typeof msg === 'string' ? msg : JSON.stringify(msg));
    } finally {
      setIsLoading(false);
    }
  }, [queryParams, pageSize]);

  useEffect(() => {
    loadCourses();
  }, [loadCourses]);

  const handleArchive = async (id) => {
    try {
      const res = await archiveCourse(id);
      appToast.success(res?.message || 'Course status updated successfully.');
      loadCourses();
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Failed to update course status.';
      appToast.error(typeof msg === 'string' ? msg : 'Failed to update course status.');
    }
  };

  const handleDelete = async (id) => {
    const confirmed = await appConfirm({
      title: 'Delete Course',
      message: 'Are you sure you want to permanently delete this course?',
      confirmText: 'Delete',
      variant: 'danger',
    });
    if (!confirmed) return;

    try {
      await deleteCourse(id);
      appToast.success('Course deleted successfully.');
      loadCourses();
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Failed to delete course.';
      appToast.error(typeof msg === 'string' ? msg : 'Failed to delete course.');
    }
  };

  const resetFilters = () => {
    setSearchText('');
    setPage(1);
  };

  const actions = {
    setViewMode: (mode) => {
      setViewMode(mode);
      setPage(1);
    },
    setSearchText: (text) => {
      setSearchText(text);
      setPage(1);
    },
    setPage,
    setPageSize,
    resetFilters,
    handleArchive,
    handleDelete,
    refresh: loadCourses,
  };

  return {
    state: {
      viewMode,
      searchText,
      page,
      pageSize,
      isLoading,
      error,
      rows: items,
      totalCount: meta.totalCount,
      totalPages: meta.totalPages,
      meta,
    },
    actions,
  };
}
